import {Component, forwardRef, Input} from '@angular/core';
import {CacheDataService} from '../../api/services/cache-data.service';
import {FlatTreeControl} from '@angular/cdk/tree';
import {Occupation} from '../../api/models/occupation';
import {TreeViewSelectionNode} from './helpers/tree-view-selection-node';
import {TreeViewDataSource} from './helpers/tree-vew-data-source';
import {FilterEditorComponent} from './filter-editor/filter-editor.component';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {OccupationKey} from '../../api/models/occupation-key';
import {Attribute_Category} from '../../api/models/attribute-_category';
import {AttributeKey} from '../../api/models/attribute-key';

@Component({
  selector: "attribute-filter-editor",
  templateUrl: "./filter-editor/filter-editor.component.html",
  styleUrls: ["./filter-editor/filter-editor.component.css"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AttributeFilterEditorComponent),
      multi: true
    }
  ]
})
export class AttributeFilterEditorComponent extends FilterEditorComponent<Attribute_Category, AttributeKey, TVSNAttribute, AttributeDataSource> implements ControlValueAccessor {

  constructor(cacheDataService: CacheDataService) {
    super(cacheDataService);
  }

  protected getNewDataSource(treeControl: FlatTreeControl<TVSNAttribute, TVSNAttribute>, cacheDataService: CacheDataService): AttributeDataSource {
    return new AttributeDataSource(treeControl, cacheDataService);
  }
}

export class AttributeDataSource extends TreeViewDataSource<Attribute_Category, AttributeKey, TVSNAttribute> {

  constructor(
    treeControl: FlatTreeControl<TVSNAttribute>,
    public cacheDataService: CacheDataService) {
    super(treeControl);
  }

  protected loadInitialData(callback: (results: TVSNAttribute[]) => void) {
    this.cacheDataService.cacheDataGetAttributeTypes({ pageNumber: 1, pageSize: 1000 }).subscribe(result => {
      const nodes = result.page.map(item => new TVSNAttribute(
        [], this.blankCover(item.attributeType), 1,
        {
          attributeType: item.attributeType,
          attributeSubType: undefined,
          attributeCategory: undefined
        },
        undefined, true, true
      ));
      callback(nodes);
    });
  }

  protected loadChildNodes(node: TVSNAttribute, callback: (results: TVSNAttribute[]) => void) {
    if (node.level === 1) {
      this.cacheDataService.cacheDataGetAttributeSubTypesByAttributeType({ attributeType: node.sourceValue.attributeType, pageNumber: 1, pageSize: 1000 }).subscribe(result => {
        const nodes : TVSNAttribute[] = result.page.map(item => new TVSNAttribute(
          [], this.blankCover(item.attributeSubType), node.level + 1,
          {
            attributeType: item.attributeType,
            attributeSubType: item.attributeSubType,
            attributeCategory: undefined
          },
          node, true, true
        ));
        callback(nodes);
      });
    } else if (node.level === 2) {
      this.cacheDataService.cacheDataGetAttributeCategoriesByAttributeTypeAndSubType({ attributeType: node.sourceValue.attributeType, attributeSubType: node.sourceValue.attributeSubType, pageNumber: 1, pageSize: 1000 }).subscribe(result => {
        const nodes: TVSNAttribute[] = result.page.map(item => new TVSNAttribute(
          [], this.blankCover(item.attributeCategory), node.level + 1,
          {
            attributeType: item.attributeType,
            attributeSubType: item.attributeSubType,
            attributeCategory: item.attributeCategory
          },
          node, false, true
        ));
        callback(nodes);
      });
    }
  }
}

export class TVSNAttribute extends TreeViewSelectionNode<Attribute_Category, AttributeKey> {

  protected deriveKey(): AttributeKey {
    return {
      attributeType: this.sourceValue.attributeType,
      attributeSubType: this.sourceValue.attributeSubType,
      attributeCategory: this.sourceValue.attributeCategory
    };
  }

  protected isAnyKnownChildSelected(allKnownSelections: AttributeKey[]): boolean {
    // Rules:
    // If attributeCategory != null, there can be no children
    // If current level matches, it must NOT match entirely (i.e, there target should have a lower level than THIS node

    if (!this.sourceValue.attributeCategory) {
      if (this.sourceValue.attributeSubType) {
        return allKnownSelections?.some(x => x.attributeSubType == this.sourceValue.attributeSubType && x.attributeCategory);
      } else if (this.sourceValue.attributeType) {
        return allKnownSelections?.some(x => x.attributeType === this.sourceValue.attributeType && x.attributeSubType);
      }
    }

    return false;
  }

  public isKeyForMe(key: AttributeKey): boolean {
    return key.attributeCategory == this.sourceValue.attributeCategory
    && key.attributeSubType === this.sourceValue.attributeSubType
    && key.attributeType === this.sourceValue.attributeType;
  }

  public isChildOf(key: AttributeKey): boolean {
    if (key.attributeSubType != null) {
      return key.attributeSubType === this.sourceValue.attributeSubType
        && key.attributeType === this.sourceValue.attributeType;
    } else if (key.attributeType != null) {
      return key.attributeType === this.sourceValue.attributeType;
    }

    return false;
  }

  public toSourceKey(): AttributeKey {
    return {
      attributeType: this.sourceValue.attributeType,
      attributeSubType: this.sourceValue.attributeSubType,
      attributeCategory: this.sourceValue.attributeCategory
    }
  }

  public matchesKey(key: AttributeKey): boolean {
    return key.attributeCategory === this.sourceValue.attributeCategory
    && key.attributeSubType == this.sourceValue.attributeSubType
    && key.attributeType === this.sourceValue.attributeType;
  }

}
